<template>
  <v-menu v-model="menu" :open-delay="0" :close-on-content-click="false" width="650">
    <template #activator="{ props }">
      <v-badge :color="allHealthyColor" dot offset-y="6">
        <v-btn variant="text" size="30" rounded="rounded" icon="mdi-server" v-bind="props"></v-btn>
      </v-badge>
    </template>

    <v-card flat :loading="serviceHealthCheck.fetch.state.pending">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>
          {{ $t('ServiceStatusTitle') }}
          <DebugHintIcon :to-json="serviceHealthCheck.statusResults.value" position="left" eager></DebugHintIcon>
        </span>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <!-- Error State -->
        <FetchErrorRetry v-if="serviceHealthCheck.fetch.state.error" :error="serviceHealthCheck.fetch.state.error" />
        <!--        <FetchErrorRetry v-if="true" :error="'serviceHealthCheck.fetch.state.error'" />-->

        <v-list v-else density="compact">
          <v-list-item v-for="(statusResult, key) in serviceHealthCheck.statusResults.value" :key="key">
            <template #title>
              {{ getServiceName(key) }}
            </template>

            <template #append>
              <div class="d-flex align-center align-end ga-1">
                <small class="text-grey"> {{ statusResult.response_time_ms }}ms </small>
                <v-chip :color="statusResult.status === 'healthy' ? 'success' : 'error'" size="small" variant="flat">
                  {{ getStatusName(statusResult.status) }}
                  <InfoHintIcon> {{ statusResult.message }} </InfoHintIcon>
                </v-chip>
              </div>
            </template>
          </v-list-item>
        </v-list>

        <!-- Websocket-->
        <div v-if="user.user.value" class="d-flex justify-center mb-2">
          <v-tooltip location="top" open-delay="500" :text="$t('ServiceStatusWebsocketHint')">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                class="text-grey"
                variant="plain"
                size="small"
                density="compact"
                :loading="wsConnecting"
                @click="toggleWebsocketConnection"
                >{{ $t('ServiceStatusWebsocketConnection') }}
                <span
                  class="ws-status-dot ml-1"
                  :class="{
                    'ws-status-dot--live': wsConnected,
                    'ws-status-dot--connecting': wsConnecting,
                  }"
                ></span>
              </v-btn>
            </template>
          </v-tooltip>
          <v-btn-icon
            :to="{ name: Routes._websocket }"
            icon="mdi-chevron-right"
            variant="plain"
            size="small"
            density="compact"
            @click="menu = false"
          ></v-btn-icon>
        </div>
      </v-card-text>

      <!-- Overall Status -->
      <v-divider />
      <v-card-actions class="bg-surface">
        <v-btn
          icon="mdi-refresh"
          size="small"
          :loading="serviceHealthCheck.fetch.state.pending"
          @click.stop="serviceHealthCheck.fetch.refresh"
        />
        <small class="text-grey"> {{ $t('ServiceStatusLastChecked') }}: {{ lastCheckedTime }} </small>
        <v-spacer />

        <v-chip :color="allHealthyColor" :prepend-icon="allHealthyIcon" variant="flat">
          {{ allHealthyText }}
        </v-chip>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { useServiceHealthCheck } from '~/composables/common/useServiceHealthCheck'
import FetchErrorRetry from '~/components/Common/FetchErrorRetry.vue'
import { ServiceHealthStatusEnum } from '@gridcon-energy/schaltungstool-backend'
import { formatTimeAgoIntl, useTimeoutPoll, watchImmediate } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import InfoHintIcon from '~/components/Common/InfoHintIcon.vue'
import DebugHintIcon from '~/components/Common/DebugHintIcon.vue'
import { injectWebsocketClient } from '~/api/websocket'
import useUser from '~/composables/useUser'
import { Routes } from '~/_routes'

export default defineComponent({
  name: 'BackendServicesStatusMenu',
  components: {
    DebugHintIcon,
    InfoHintIcon,
    FetchErrorRetry,
  },
  setup() {
    const menu = ref(false)
    const serviceHealthCheck = useServiceHealthCheck()
    const lastCheckedTime = ref('-' as string)
    const ws = injectWebsocketClient()
    const user = useUser()

    const i18n = useI18n()
    const timeAgoOptions = computed(() => ({ locale: i18n.locale.value }))

    function updateLastChecked() {
      if (!serviceHealthCheck.fetch.state.timestamp) {
        lastCheckedTime.value = '-'
      } else {
        lastCheckedTime.value = formatTimeAgoIntl(
          new Date(serviceHealthCheck.fetch.state.timestamp),
          timeAgoOptions.value,
        )
      }
    }

    const { pause, resume } = useTimeoutPoll(updateLastChecked, 1000)
    watchImmediate(menu, (value) => {
      if (value) {
        updateLastChecked()
        resume()
      } else {
        pause()
      }
    })

    return {
      Routes,
      ServiceHealthStatusEnum,
      menu,
      user,
      serviceHealthCheck,
      lastCheckedTime,
      wsConnected: ws.connected,
      wsConnecting: ws.connecting,
      toggleWebsocketConnection: ws.toggleConnection,
    }
  },
  computed: {
    allHealthyColor() {
      return this.serviceHealthCheck.forCurrentStatus('success', 'warning', 'error', 'grey')
    },
    allHealthyIcon() {
      return this.serviceHealthCheck.forCurrentStatus('mdi-check-circle', 'mdi-alert-circle', 'mdi-alert-circle', '')
    },
    allHealthyText() {
      return this.serviceHealthCheck.forCurrentStatus(
        this.$t('ServiceStatusAllHealthy'),
        this.$t('ServiceStatusSomeUnhealthy'),
        this.$t('ServiceStatusFatal'),
        this.$t('ServiceStatusUndefined'),
      )
    },
  },
  methods: {
    getServiceName(serviceName: string): string {
      return this.$t(`ServiceHealthChecks.${serviceName}`)
    },
    getStatusName(status: ServiceHealthStatusEnum): string {
      return status === ServiceHealthStatusEnum.Healthy
        ? this.$t('ServiceStatusHealthy')
        : this.$t('ServiceStatusUnhealthy')
    },
  },
})
</script>

<style scoped lang="scss">
.ws-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgb(var(--v-theme-on-surface));
  opacity: 0.35;
}

.ws-status-dot--live {
  background: rgb(var(--v-theme-success));
  opacity: 1;
  animation: ws-pulse 1.6s ease-in-out infinite;
}

.ws-status-dot--connecting {
  width: 12px;
  height: 12px;
  background: transparent;
  opacity: 1;
  border: 2px solid rgba(var(--v-theme-on-surface), 0.25);
  border-top-color: rgb(var(--v-theme-on-surface));
  animation: ws-spin 0.8s linear infinite;
}

@keyframes ws-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes ws-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
